import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TranscriptComponent } from '../../ConversationTranscript/types';
import { buildConversationAudit } from './buildAudit';
import {
  useGetApiEventsQuery,
  useGetErrorEventsQuery,
} from 'src/services/error-events/errorEventsApi';

const EMPTY_FILTER = '';

// Errors and failed API calls both come from the same 30-day error stream, so a
// conversation older than that window has no error record to show — an absence
// state, not a healthy conversation.
export const ERROR_RETENTION_DAYS = 30;

export const isOutsideErrorRetention = (startDatetime?: string | null) => {
  if (!startDatetime) {
    return false;
  }
  const started = Date.parse(
    startDatetime.endsWith('Z') ? startDatetime : startDatetime + 'Z',
  );
  if (!Number.isFinite(started)) {
    return false;
  }
  return Date.now() - started > ERROR_RETENTION_DAYS * 24 * 60 * 60 * 1000;
};

export const useConversationAudit = ({
  conversationId,
  transcriptComponents,
}: {
  conversationId?: string;
  transcriptComponents?: TranscriptComponent[];
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const skip = !conversationId;

  const { data: errorEventsData, isFetching: isFetchingErrors } =
    useGetErrorEventsQuery(
      {
        channel: EMPTY_FILTER,
        conversation_id: conversationId ?? '',
        error_id: EMPTY_FILTER,
        error_message: EMPTY_FILTER,
        error_payload: EMPTY_FILTER,
        page: 1,
        page_size: '100',
        product: EMPTY_FILTER,
        severity: EMPTY_FILTER,
        sort_column: 'timestamp',
        sort_direction: 'asc',
      },
      { skip },
    );

  const { data: apiEventsData, isFetching: isFetchingApiEvents } =
    useGetApiEventsQuery(
      {
        conversation_id: conversationId ?? '',
        page: 1,
        page_size: '100',
        sort_direction: 'asc',
        url: EMPTY_FILTER,
      },
      { skip },
    );

  const audit = useMemo(
    () =>
      buildConversationAudit({
        apiEvents: apiEventsData?.api_events ?? [],
        errorEvents: errorEventsData?.error_events ?? [],
        t,
        transcriptComponents: transcriptComponents ?? [],
      }),
    [apiEventsData, errorEventsData, t, transcriptComponents],
  );

  return { audit, isLoading: isFetchingErrors || isFetchingApiEvents };
};
